import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const RequestsApi = createApi({
  reducerPath: "Requests",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BACKEND_URL}`,
    credentials: "include",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      // Backend resolves the village display language from this header
      // (getServiceRequestById reads it directly — no i18n middleware here).
      headers.set("Accept-Language", localStorage.getItem("lang") === "ar" ? "ar" : "en");
      return headers;
    },
    credentials: "include",
  }),
  endpoints: (builder) => ({
    getAllRequests: builder.query({
      query: (val) => {
        const page = val.page ?? 1;
        const limit = val.limit ?? 10;
        const sortBy = val.sortby ? `&sortBy=${val.sortby}` : "";
        const order = val.order ? `&order=${val.order}` : "";
        const search = val.search ? `&search=${val.search}` : "";
        const queryString = val.queryString
          ? `&${val.queryString.substring(1)}`
          : "";
        return `/service-requests?page=${page}&limit=${limit}${search}${sortBy}${order}${queryString}`;
      },
    }),
    getSpecificRequest: builder.query({
      query: (id) => `/service-requests/${id}`,
    }),
    getServiceRequestTasks: builder.query({
      query: (id) => `/service-requests/${id}/tasks`,
    }),
    cancelRequest: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/service-requests/cancel/${id}`,
        method: "PUT",
        body: { reason },
      }),
    }),
    getServiceRequestCancellations: builder.query({
      query: (val) => {
        const page = val.page ?? 1;
        const limit = val.limit ?? 10;
        const sortBy = val.sortby ? `&sortBy=${val.sortby}` : "";
        const order = val.order ? `&order=${val.order}` : "";
        const search = val.search ? `&search=${val.search}` : "";
        const status = val.status ? `&status=${val.status}` : "";
        return `/service-requests/cancellations?page=${page}&limit=${limit}${search}${sortBy}${order}${status}`;
      },
    }),
    approveCancellationRequest: builder.mutation({
      query: (id) => ({
        url: `/service-requests/cancellations/${id}/approve`,
        method: "PUT",
      }),
    }),
    rejectCancellationRequest: builder.mutation({
      query: (id) => ({
        url: `/service-requests/cancellations/${id}/reject`,
        method: "PUT",
      }),
    }),
    deleteRequest: builder.mutation({
      query: (id) => ({
        url: `/service-requests/${id}`,
        method: "DELETE",
      }),
    }),
    createRequest: builder.mutation({
      query: (payload) => ({
        url: "/service-requests/create",
        method: "POST",
        body: payload,
      }),
    }),
    editRequest: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/service-requests/edit/${id}`,
        method: "PUT",
        body: payload,
      }),
    }),
    getRequestsByStatus: builder.query({
      query: ({ status, val }) =>
        `/service-requests/reports/${status}?page=${val.page}&limit=${
          val.limit
        }${val.sortby ? `&sortBy=${val.sortby}` : ""}${
          val.search ? `&search=${val.search}` : ""
        }${val.fromDate ? `&fromDate=${val.fromDate}` : ""}${
          val.toDate ? `&toDate=${val.toDate}` : ""
        }`,
    }),
  }),
});

export const {
  useGetAllRequestsQuery,
  useCancelRequestMutation,
  useDeleteRequestMutation,
  useGetSpecificRequestQuery,
  useGetServiceRequestTasksQuery,
  useCreateRequestMutation,
  useEditRequestMutation,
  useGetRequestsByStatusQuery,
  useGetServiceRequestCancellationsQuery,
  useApproveCancellationRequestMutation,
  useRejectCancellationRequestMutation,
} = RequestsApi;
